/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { FaLock, FaTimesCircle } from 'react-icons/fa';
import { getId, canAccessItem, canEditItemForItem } from '../../utils/item';
import { deepCloneArray } from '../../utils/array';
import axios from 'axios';
import { ConditionGlobal } from '../condition/condition.Global';
import { DateGlobal } from '../date/date.Global';
import { LinkGlobal } from '../link/link.Global';
import { NumGlobal } from '../num/num.Global';
import { PicGlobal } from '../pic/pic.Global';
import { TextGlobal } from '../text/text.Global';
import { isReadOnly } from '../../utils/roles';
import { canUseMjForItem, isMjForItem } from '../../utils/auth';
import { useDispatch, useSelector } from 'react-redux';
import { IframeAll } from '../iframe/iframe.All';
import { FilterAll } from '../filter/filter.All';
import useItems from '../../hooks/useItems';
import { TimelineGlobal } from '../timeline/timeline.Global';
import { DiceGlobal } from '../dice/dice.Global';
import { GeneratorGlobal } from '../generator/generator.Global';
import { getItemGenerators, update } from '../../services/item';
import { saveAll } from '../../store/items';

const isValidObjectId = id =>
  typeof id === 'string' && /^[a-f0-9]{24}$/i.test(id);

export const GetField = props => {
  const {
    fieldId,
    itemId,
    fieldInGrid,
    getTextHeight,
    boardTabId,
    boardType,
    gridLayoutKey,
    canEditOverride,
  } = props;

  const [item] = useItems([itemId]);
  const boardTab = useSelector(state => state.items?.[boardTabId]);
  const linkedFieldItem = useSelector(state => state.items?.[fieldId]);
  const dispatch = useDispatch();
  const [campaign, world, universe] = useItems([
    item?.campaign,
    item?.world,
    item?.universe,
  ]);
  const permissionItems = React.useMemo(() => {
    const map = {};
    if (item?._id) map[item._id] = item;
    if (campaign?._id) map[campaign._id] = campaign;
    if (world?._id) map[world._id] = world;
    if (universe?._id) map[universe._id] = universe;
    return map;
  }, [item, campaign, world, universe]);

  const [field, setField] = useState({});
  const canEdit =
    typeof canEditOverride === 'boolean'
      ? canEditOverride
      : canEditItemForItem(item, permissionItems);
  const checkedMissingRef = React.useRef(new Set());
  const loadingGeneratorsByItemRef = React.useRef(new Set());

  useEffect(() => {
    if (fieldInGrid?.type !== 'link') return;
    if (!boardTabId || !fieldId) return;
    if (!item?._id) return;
    if (linkedFieldItem?._id) return;
    if (!/^[a-f0-9]{24}$/i.test(fieldId)) return;
    const checkKey = `${boardTabId}:${fieldId}`;
    if (checkedMissingRef.current.has(checkKey)) return;
    checkedMissingRef.current.add(checkKey);

    let cancelled = false;
    const checkAndCleanupMissingLink = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/${fieldId}`,
        );
        if (cancelled) return;
        if (res?.data?._id) {
          dispatch(saveAll({ item: res.data, log: 'field-get-link-check' }));
        }
      } catch (error) {
        if (cancelled) return;
        if (error?.response?.status !== 404) {
          // Unknown/transient/access error: don't remove widget.
          return;
        }
        const latestTab = boardTab;
        if (!latestTab?.grid) return;
        const clonedGrid = deepCloneArray(latestTab.grid);
        const nextGrid = clonedGrid.filter(element => !element?.[fieldId]);
        if (nextGrid.length === clonedGrid.length) return;
        update(`items/${boardTabId}`, 'grid', nextGrid);
      }
    };
    checkAndCleanupMissingLink();

    return () => {
      cancelled = true;
    };
  }, [
    fieldInGrid?.type,
    boardTabId,
    fieldId,
    item?._id,
    linkedFieldItem?._id,
    boardTab,
    dispatch,
  ]);

  useEffect(() => {
    if (fieldInGrid?.type !== 'generator') {
      return;
    }
    if (!item?._id || !fieldId) {
      return;
    }
    const hasGenerator = Array.isArray(item?.generators)
      ? item.generators.some(entry => getId(entry) === fieldId)
      : false;
    if (hasGenerator) {
      return;
    }
    const itemKey = String(item._id);
    if (loadingGeneratorsByItemRef.current.has(itemKey)) {
      return;
    }
    loadingGeneratorsByItemRef.current.add(itemKey);
    getItemGenerators(item._id)
      .catch(() => null)
      .finally(() => {
        loadingGeneratorsByItemRef.current.delete(itemKey);
      });
  }, [fieldInGrid?.type, item?._id, item?.generators, fieldId]);

  useEffect(() => {
    if (item?._id) {
      let tmpField = { error: true };
      if (fieldInGrid.type === 'has_num' || fieldInGrid.type === 'num') {
        let arrNumIds = [];
        for (const objNum of item.has_num || []) {
          arrNumIds.push(getId(objNum));
        }
        const idx = arrNumIds.findIndex(id => id === fieldId);
        tmpField = item?.has_num?.[idx]?.[fieldId];
      }
      if (fieldInGrid.type === 'pics' || fieldInGrid.type === 'pic') {
        let arrPicIds = [];
        for (const objPic of item.pics || []) {
          arrPicIds.push(getId(objPic));
        }
        const idx = arrPicIds.findIndex(id => id === fieldId);
        tmpField = item?.pics?.[idx]?.[fieldId];
      }
      if (fieldInGrid.type === 'text') {
        let arrTextIds = [];
        for (const objText of item.text || []) {
          arrTextIds.push(getId(objText));
        }
        const idx = arrTextIds.findIndex(id => id === fieldId);
        tmpField = item.text?.[idx]?.[fieldId];
      }
      if (fieldInGrid.type === 'conditions') {
        let arrConditionIds = [];
        for (const objCondition of item.conditions || []) {
          arrConditionIds.push(getId(objCondition));
        }
        const idx = arrConditionIds.findIndex(id => id === fieldId);
        tmpField = item.conditions?.[idx]?.[fieldId];
      }
      if (fieldInGrid.type === 'link') {
        tmpField = {};
      }
      if (fieldInGrid.type === 'dates') {
        let arrDateIds = [];
        for (const objDate of item.dates || []) {
          arrDateIds.push(getId(objDate));
        }
        const idx = arrDateIds.findIndex(id => id === fieldId);
        tmpField = item.dates?.[idx]?.[fieldId];
      }

      if (fieldInGrid.type === 'iframes') {
        let arrIframeIds = [];
        for (const objIframe of item.iframes || []) {
          arrIframeIds.push(getId(objIframe));
        }
        const idx = arrIframeIds.findIndex(id => id === fieldId);
        tmpField = item.iframes?.[idx]?.[fieldId];
      }

      if (fieldInGrid.type === 'filters') {
        let arrFilterIds = [];
        for (const objFilter of item.filters || []) {
          arrFilterIds.push(getId(objFilter));
        }
        const idx = arrFilterIds.findIndex(id => id === fieldId);
        tmpField = item.filters?.[idx]?.[fieldId];
      }
      if (fieldInGrid.type === 'timeline' || fieldInGrid.type === 'timelines') {
        let arrTimelineIds = [];
        for (const objTimeline of item.timelines || []) {
          arrTimelineIds.push(getId(objTimeline));
        }
        const idx = arrTimelineIds.findIndex(id => id === fieldId);
        tmpField = item.timelines?.[idx]?.[fieldId];
      }

      if (fieldInGrid.type === 'dice') {
        let arrDiceIds = [];
        for (const objDice of item.dices || []) {
          arrDiceIds.push(getId(objDice));
        }
        const idx = arrDiceIds.findIndex(id => id === fieldId);
        tmpField = item.dices?.[idx]?.[fieldId];
      }

      if (fieldInGrid.type === 'generator') {
        let arrGeneratorIds = [];
        for (const objGenerator of item.generators || []) {
          arrGeneratorIds.push(getId(objGenerator));
        }
        const idx = arrGeneratorIds.findIndex(id => id === fieldId);
        const nextGeneratorField = item.generators?.[idx]?.[fieldId];
        // Keep previous generator state while dedicated generators payload hydrates.
        if (!nextGeneratorField && field && !field?.error) {
          tmpField = field;
        } else {
          tmpField = nextGeneratorField;
        }
      }

      setField(tmpField);
    }
  }, [item]);

  const lockedField = () => {
    return (
      <div
        aria-label='Champ verrouillé'
        className='text-secondary w-full h-full flex items-center justify-center opacity-25 border-secondary border'
      >
        <FaLock size={20} />
      </div>
    );
  };

  if (item?._id && item.__partial) {
    return (
      <div
        className='animate-pulse p-05'
        style={{ height: '100%', width: '100%' }}
      >
        <div
          className='bg-primary-d rounded'
          style={{ height: '100%', width: '100%' }}
        ></div>
      </div>
    );
  }

  const hasMissingAccessScope =
    (item?.type !== 'campaign' &&
      isValidObjectId(item?.campaign) &&
      !campaign?._id) ||
    (isValidObjectId(item?.world) && !world?._id) ||
    (isValidObjectId(item?.universe) && !universe?._id);

  const canMj =
    isMjForItem(item, permissionItems) || canUseMjForItem(item, permissionItems);
  if (!canMj && !isReadOnly()) {
    if (hasMissingAccessScope) {
      return lockedField();
    }
    if (!canAccessItem(item, campaign, world, universe)) {
      return lockedField();
    }
  }
  if (
    (fieldInGrid.type === 'num' || fieldInGrid.type === 'has_num') &&
    item?._id &&
    field?.label
  ) {
    return (
      <NumGlobal
        numId={fieldId}
        num={field}
        itemId={itemId}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }
  if (
    (fieldInGrid.type === 'pic' || fieldInGrid.type === 'pics') &&
    item?._id &&
    field?.url
  ) {
    return (
      <PicGlobal
        itemId={itemId}
        picId={fieldId}
        pic={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }

  if (
    fieldInGrid.type === 'text' &&
    item?._id &&
    field &&
    typeof field.text === 'string'
  ) {
    const rawDisplayType = fieldInGrid?.displayType || 'hauteur dynamique';
    const displayType =
      boardType === 'draw' && rawDisplayType === 'hauteur dynamique'
        ? 'standard'
        : rawDisplayType;
    const isLabel = displayType === 'label';
    const shouldMeasureHeight = displayType === 'hauteur dynamique';
    return (
      <div
        className={`w-full h-full p-1 ${
          isLabel ? 'flex items-center justify-center' : 'flex items-center'
        }`}
      >
        <TextGlobal
          itemId={itemId}
          textId={fieldId}
          text={field}
          displayType={displayType}
          getTextHeight={shouldMeasureHeight ? getTextHeight : null}
          boardTabId={boardTabId}
          gridLayoutKey={gridLayoutKey}
          canEdit={canEdit}
        />
      </div>
    );
  }

  if (fieldInGrid.type === 'conditions' && item?._id) {
    return (
      <ConditionGlobal
        itemId={itemId}
        conditionId={fieldId}
        condition={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }

  if (fieldInGrid.type === 'divider') {
    return (
      <div className='h-full w-full flex items-center'>
        <div
          className={`${
            fieldInGrid.displayType !== 'none' ? 'border-primary border-t' : ''
          } ${
            fieldInGrid.displayType === 'dashed' ? 'border-dashed' : ''
          }  flex-1`}
        ></div>
      </div>
    );
  }

  if (fieldInGrid.type === 'link') {
    return (
      <LinkGlobal
        displayType={fieldInGrid.displayType}
        itemId={itemId}
        linkId={fieldId}
        elementsToDisplay={fieldInGrid.elementsToDisplay}
        fromBoard={true}
        getTextHeight={getTextHeight}
        fieldId={fieldId}
        gridLayoutKey={gridLayoutKey}
        canEdit={canEdit}
        enableDrag={boardType !== 'map'}
      />
    );
  }

  if (fieldInGrid.type === 'dates' && item?._id) {
    return (
      <DateGlobal
        itemId={itemId}
        dateId={fieldId}
        date={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }

  if (fieldInGrid.type === 'iframes' && item?._id) {
    return (
      <IframeAll
        itemId={itemId}
        iframeId={fieldId}
        iframe={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }

  if (fieldInGrid.type === 'filters' && item?._id) {
    return (
      <FilterAll
        displayType={fieldInGrid.displayType}
        itemId={itemId}
        filterId={fieldId}
        filter={field}
        boardTabId={boardTabId}
        canEdit={canEdit}
        disableItemDrag={
          boardType === 'draw' || fieldInGrid?.filterDragLocked === true
        }
      />
    );
  }

  if (
    (fieldInGrid.type === 'timeline' || fieldInGrid.type === 'timelines') &&
    item?._id &&
    field
  ) {
    return (
      <TimelineGlobal
        itemId={itemId}
        timelineId={fieldId}
        timeline={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
        boardType={boardType}
      />
    );
  }

  if (fieldInGrid.type === 'dice' && item?._id && field) {
    return (
      <DiceGlobal
        itemId={itemId}
        diceId={fieldId}
        dice={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }

  if (fieldInGrid.type === 'generator' && item?._id && field) {
    return (
      <GeneratorGlobal
        itemId={itemId}
        generatorId={fieldId}
        generator={field}
        fieldInGrid={fieldInGrid}
        canEdit={canEdit}
      />
    );
  }

  if (field?.error) {
    return (
      <div className='text-secondary opacity-30 flex items-center justify-center h-full'>
        <FaTimesCircle size={30} />
      </div>
    );
  }

  return (
    <div
      className='animate-pulse p-05'
      style={{ height: '100%', width: '100%' }}
    >
      <div
        className='bg-primary-d rounded'
        style={{ height: '100%', width: '100%' }}
      ></div>
    </div>
  );
};
